import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { SubscriptionCheckout } from '@/components/billing'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useToast } from '@/hooks/use-toast'
import { Loader2, CreditCard, AlertTriangle } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

const MONTHLY_PRICE = 500

type SubscriptionStatus =
  | 'incomplete'
  | 'incomplete_expired'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'paused'

interface Subscription {
  id: string
  account_id: string
  stripe_customer_id: string
  stripe_subscription_id: string | null
  stripe_price_id: string | null
  status: SubscriptionStatus
  current_period_start: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  canceled_at: string | null
  created_at: string
  updated_at: string
}

function getStatusBadge(status: SubscriptionStatus, cancelAtPeriodEnd: boolean) {
  if (cancelAtPeriodEnd && status === 'active') {
    return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Canceling</Badge>
  }

  switch (status) {
    case 'active':
    case 'trialing':
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
    case 'past_due':
      return <Badge variant="destructive">Past Due</Badge>
    case 'canceled':
      return <Badge variant="secondary">Canceled</Badge>
    case 'unpaid':
      return <Badge variant="destructive">Unpaid</Badge>
    case 'incomplete':
    case 'incomplete_expired':
      return <Badge variant="secondary">Incomplete</Badge>
    case 'paused':
      return <Badge variant="outline">Paused</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

function formatDate(dateString: string | null) {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function BillingPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Handle success/cancel URL params
  useEffect(() => {
    const success = searchParams.get('success')
    const canceled = searchParams.get('canceled')

    if (success === 'true') {
      toast({
        title: 'Subscription activated',
        description: 'Your subscription is now active. Thank you for subscribing!',
      })
      setSearchParams({})
    } else if (canceled === 'true') {
      toast({
        title: 'Checkout canceled',
        description: 'You canceled the checkout process. No charges were made.',
        variant: 'destructive',
      })
      setSearchParams({})
    }
  }, [searchParams, setSearchParams, toast])

  // Fetch subscription
  const { data: subscription, isLoading, refetch } = useQuery({
    queryKey: ['subscription', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return null

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('account_id', activeAccount.id)
        .maybeSingle()

      if (error) {
        throw error
      }

      return data as Subscription | null
    },
    enabled: !!activeAccount?.id,
  })

  // Handle successful subscription
  const handleSubscriptionSuccess = () => {
    refetch()
    toast({
      title: 'Subscription activated',
      description: 'Your subscription is now active. Thank you for subscribing!',
    })
  }

  // Create portal session mutation
  const portalMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-portal-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({}),
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create portal session')
      }

      const { url } = await response.json()
      return url
    },
    onSuccess: (url) => {
      // Redirect to Stripe Customer Portal
      window.location.href = url
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-32" />
          </CardContent>
        </Card>
      </div>
    )
  }

  const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing'
  const isPastDue = subscription?.status === 'past_due'
  const isCanceled = subscription?.status === 'canceled'
  const isIncomplete = subscription?.status === 'incomplete' || subscription?.status === 'incomplete_expired'
  const isCanceling = subscription?.cancel_at_period_end && hasActiveSubscription
  const shouldShowCheckout = !subscription || isCanceled || isIncomplete

  return (
    <div className="space-y-6">
      {/* Past Due Warning */}
      {isPastDue && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Payment Failed</AlertTitle>
          <AlertDescription>
            Your last payment failed. Please update your payment method to avoid service interruption.
          </AlertDescription>
        </Alert>
      )}

      {/* Canceling Notice */}
      {isCanceling && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Subscription Canceling</AlertTitle>
          <AlertDescription>
            Your subscription will end on {formatDate(subscription?.current_period_end || null)}.
            You can reactivate anytime before then.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription
          </CardTitle>
          <CardDescription>
            Manage your LimoBuilder subscription and billing
          </CardDescription>
        </CardHeader>
        <CardContent>
          {shouldShowCheckout ? (
            // No active subscription - show checkout form directly
            <SubscriptionCheckout onSuccess={handleSubscriptionSuccess} />
          ) : (
            // Has subscription - show details
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <div className="text-sm text-muted-foreground">Status</div>
                  <div className="mt-1 flex items-center gap-2">
                    {getStatusBadge(subscription.status, subscription.cancel_at_period_end)}
                  </div>
                </div>

                <div className="rounded-lg border p-4">
                  <div className="text-sm text-muted-foreground">Monthly Price</div>
                  <div className="mt-1 text-2xl font-semibold">${MONTHLY_PRICE}</div>
                </div>

                <div className="rounded-lg border p-4">
                  <div className="text-sm text-muted-foreground">Current Period</div>
                  <div className="mt-1 text-sm">
                    {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
                  </div>
                </div>

                <div className="rounded-lg border p-4">
                  <div className="text-sm text-muted-foreground">
                    {isCanceling ? 'Access Ends' : 'Next Billing Date'}
                  </div>
                  <div className="mt-1 text-sm">
                    {formatDate(subscription.current_period_end)}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  onClick={() => portalMutation.mutate()}
                  disabled={portalMutation.isPending}
                  variant={isPastDue ? 'default' : 'outline'}
                >
                  {portalMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isPastDue ? 'Update Payment Method' : 'Manage Subscription'}
                </Button>

                <Button
                  variant="ghost"
                  onClick={() => refetch()}
                >
                  Refresh Status
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Impersonation Notice */}
      {isImpersonating && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Admin Preview Mode</AlertTitle>
          <AlertDescription>
            You are viewing this account&apos;s billing page as an admin.
            Billing actions are available but will affect the actual account.
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}
